import { useEffect, useState } from 'react';

import { useAuthContext } from '@/contexts/AuthContext';
import { useFirebaseContext } from '@/contexts/FirebaseContext';

import sessionStorageService from '../../../utility/sessionStorageService';
import { USER_SEEN_CAMPAIGN_ANNOUNCEMENTS } from '../constants';

function useCampaignAnnouncements() {
  const [campaignAnnouncementsSeenData, setCampaignAnnouncementsSeenData] =
    useState(null);
  const [loading, setLoading] = useState(false);
  const [collectionDocRef, setCollectionDocRef] = useState(null);

  const { user } = useAuthContext();
  const userId = user?._id;

  const { firestoreUtils } = useFirebaseContext();

  const fetchUserSeenAnnouncements = async (docRef) => {
    const data = await firestoreUtils?.fetchDoc(docRef);
    const announcementsData = data || null;

    // Store in session storage to reduce number of reads on subsequent homepage lands
    if (announcementsData) {
      sessionStorageService.setItem(
        USER_SEEN_CAMPAIGN_ANNOUNCEMENTS,
        JSON.stringify(announcementsData || {})
      );
    }
    return announcementsData;
  };

  const setHaveSeenAnnouncement = async (campaignId, communityId) => {
    try {
      if (!collectionDocRef) return;
      // Update firestore
      const payload = {
        [campaignId]: firestoreUtils?.addToDocArray(communityId)
      };
      await firestoreUtils?.updateDoc(collectionDocRef, payload, {
        merge: true
      });

      const savedSeenAnnouncementsDataStr = sessionStorageService.getItem(
        USER_SEEN_CAMPAIGN_ANNOUNCEMENTS
      );

      // Update session storage
      let sessionSeenAnnouncementData = {};

      if (savedSeenAnnouncementsDataStr)
        sessionSeenAnnouncementData = JSON.parse(
          savedSeenAnnouncementsDataStr
        );

      const newObj = {
        ...sessionSeenAnnouncementData,
        [campaignId]: [
          ...(sessionSeenAnnouncementData[campaignId] || []),
          communityId
        ]
      };

      sessionStorageService.setItem(
        USER_SEEN_CAMPAIGN_ANNOUNCEMENTS,
        JSON.stringify(newObj)
      );

      setCampaignAnnouncementsSeenData(newObj);
    } catch (error) {
      console.error(error);
    }
  };

  const fetchAnnouncementData = async () => {
    try {
      if (loading) return;

      setLoading(true);
      const docRef = user
        ? firestoreUtils?.createDocRef('seenCampaignAnnouncements', userId)
        : null;
      setCollectionDocRef(docRef);

      const sessionStorageSeenAnnouncements =
        sessionStorageService.getItem(USER_SEEN_CAMPAIGN_ANNOUNCEMENTS);

      // Only fetch from firestore if data doesn't exist in session storage
      const seenAnnouncements = sessionStorageSeenAnnouncements
        ? JSON.parse(sessionStorageSeenAnnouncements)
        : await fetchUserSeenAnnouncements(docRef);

      setCampaignAnnouncementsSeenData(seenAnnouncements);
      setLoading(false);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user) fetchAnnouncementData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  return {
    campaignAnnouncementsSeenData,
    loading,
    setHaveSeenAnnouncement
  };
}

export default useCampaignAnnouncements;
